import { gql, useMutation } from "@apollo/client";
import { InputField, InputTextAreaField, ShowField } from "@src/components/fields";
import Form from "@src/components/Form";
import { getCountryName } from "@src/components/select/CountrySelect";
import {
    type GQLPackageQuery,
    type GQLUpdatePackageInput,
    type GQLUpdatePackageMutation,
    type GQLUpdatePackageMutationVariables,
} from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { EndAdornment } from "./Form.sc";
// eslint-disable-next-line @comet/no-private-sibling-import
import { packageFragment } from "./fragments.gql";

export const updatePackageMutation = gql`
    mutation UpdatePackage($id: ID!, $input: UpdatePackageInput!) {
        updatePackage(id: $id, input: $input) {
            ...PackageFragment
        }
    }
    ${packageFragment}
`;

const EditForm: FC<PropsWithChildren<GQLPackageQuery>> = ({ package: adminPackage }) => {
    const intl = useIntl();
    const { id, country, type, createdAt, ...initialValues } = adminPackage;
    const validTimeInMonths = adminPackage.validTimeInMonths;
    const [updateMutation] = useMutation<GQLUpdatePackageMutation, GQLUpdatePackageMutationVariables>(updatePackageMutation);

    const submit = ({ name, description, validTimeInMonths }: GQLUpdatePackageInput) => {
        return updateMutation({
            variables: {
                id,
                input: {
                    name,
                    description,
                    validTimeInMonths: Number(validTimeInMonths),
                },
            },
        });
    };

    return (
        <Form
            mode="edit"
            onSubmit={submit}
            initialValues={initialValues}
            title={intl.formatMessage({ id: "reviermanager.packages.form.edit.title", defaultMessage: "Paket bearbeiten" })}
        >
            <ShowField label={intl.formatMessage({ id: "reviermanager.package.country", defaultMessage: "Land" })} value={getCountryName(country)} />
            <InputField name="name" label={intl.formatMessage({ id: "reviermanager.package.name", defaultMessage: "Paketname" })} />
            <InputTextAreaField
                required
                name="description"
                label={intl.formatMessage({ id: "reviermanager.package.description", defaultMessage: "Beschreibung" })}
            />
            <ShowField label={intl.formatMessage({ id: "reviermanager.package.type", defaultMessage: "Typ" })} value={type} />
            <InputField
                name="validTimeInMonths"
                type="number"
                label={intl.formatMessage({ id: "reviermanager.package.validTimeInMonths", defaultMessage: "Gültig für" })}
                endAdornment={
                    <EndAdornment>
                        <FormattedMessage
                            id="reviermanager.package.months"
                            defaultMessage="{validTimeInMonths, plural, one {Monat} other {Monate}}"
                            values={{
                                validTimeInMonths,
                            }}
                        />
                    </EndAdornment>
                }
            />
            <ShowField
                type="date"
                value={createdAt}
                label={intl.formatMessage({ id: "reviermanager.package.createdAt", defaultMessage: "Erstellt am" })}
            />
        </Form>
    );
};
export default EditForm;
